import React, { useCallback, useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, Download, UserX, UserCheck, Pencil, Loader2, KeyRound } from "lucide-react";
import { toast } from "sonner";
import api, { apiError } from "../../lib/api";
import { RelTime } from "../../lib/time";
import FeedbackDetailSheet from "../../components/FeedbackDetailSheet";
import { Button } from "../../components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../../components/ui/tabs";
import EmployeeDetailsTab from "../../components/employee/EmployeeDetailsTab";
import EmployeePerformanceTab from "../../components/employee/EmployeePerformanceTab";
import EmployeeHistoryTab from "../../components/employee/EmployeeHistoryTab";
import EmployeeEditDialog from "../../components/employee/EmployeeEditDialog";

export default function EmployeeDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [cv, setCv] = useState(null);
  const [locations, setLocations] = useState([]);
  const [editOpen, setEditOpen] = useState(false);
  const [downloadingCv, setDownloadingCv] = useState(false);
  const [openFb, setOpenFb] = useState(null);

  const load = useCallback(() => api.get(`/employees/${id}`).then((r) => setCv(r.data)).catch((e) => toast.error(apiError(e))), [id]);
  useEffect(() => {
    load();
    api.get("/locations").then((r) => setLocations(r.data)).catch((e) => console.warn("Locations load failed", e));
  }, [load]);

  const emp = cv?.employee;

  const toggleStatus = async () => {
    try {
      if (emp.status === "active") { await api.post(`/employees/${id}/deactivate`); toast.success("Unlinked from organisation"); }
      else { await api.post(`/employees/${id}/activate`); toast.success("Reactivated"); }
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const downloadCv = async () => {
    setDownloadingCv(true);
    try {
      const { data } = await api.get(`/employees/${id}/cv.pdf`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url; a.download = `${(emp?.name || "Service").replace(/\s+/g, "_")}_Servantage_CV.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Service CV generated");
    } catch (e) { toast.error(apiError(e)); }
    finally { setDownloadingCv(false); }
  };

  if (!cv) return <div className="text-slate-400">Loading…</div>;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate(-1)} className="inline-flex items-center gap-1 text-sm font-semibold text-slate-500 hover:text-slate-800" data-testid="emp-back">
            <ArrowLeft className="h-4 w-4" /> Back
          </button>
          {cv.account ? (
            <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-50 px-3 py-1 text-xs font-semibold text-emerald-700" data-testid="account-badge-has">
              <KeyRound className="h-3.5 w-3.5" />
              Has login{cv.account.method === "phone" ? " (phone)" : ""}{cv.account.last_login ? <> · active <RelTime iso={cv.account.last_login} testId="account-last-login" /></> : " · never signed in"}
            </span>
          ) : (
            <span className="inline-flex items-center gap-1.5 rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold text-slate-500" data-testid="account-badge-none">
              <KeyRound className="h-3.5 w-3.5" />
              No account yet
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={downloadCv} disabled={downloadingCv} data-testid="admin-download-cv">
            {downloadingCv ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <Download className="mr-1 h-4 w-4" />}
            {downloadingCv ? "Generating…" : "Download CV"}
          </Button>
          <Button variant="outline" onClick={() => setEditOpen(true)} disabled={emp.status !== "active"} data-testid="edit-profile-open">
            <Pencil className="mr-1 h-4 w-4" /> Edit profile
          </Button>
          <Button variant="outline" onClick={toggleStatus} data-testid="toggle-status"
            className={emp.status === "active" ? "text-red-600" : "text-emerald-600"}>
            {emp.status === "active" ? <><UserX className="mr-1 h-4 w-4" /> Unlink from org</> : <><UserCheck className="mr-1 h-4 w-4" /> Reactivate</>}
          </Button>
        </div>
      </div>

      {emp.status !== "active" && (
        <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800" data-testid="unlinked-banner">
          This employee is unlinked. Their QR and NFC tags are disabled and no changes can be made to their record. All data is retained, and they keep access to their Service CV. You can reactivate them — provided they are not currently linked to another organisation.
        </div>
      )}

      <Tabs defaultValue="details">
        <TabsList data-testid="emp-tabs">
          <TabsTrigger value="details" data-testid="tab-details">Details</TabsTrigger>
          <TabsTrigger value="performance" data-testid="tab-performance">Performance</TabsTrigger>
          <TabsTrigger value="history" data-testid="tab-history">History</TabsTrigger>
        </TabsList>

        <TabsContent value="details" className="mt-5">
          <EmployeeDetailsTab cv={cv} employeeId={id} onOpenFeedback={setOpenFb} onReload={load} />
        </TabsContent>
        <TabsContent value="performance" className="mt-5">
          <EmployeePerformanceTab cv={cv} employeeId={id} />
        </TabsContent>
        <TabsContent value="history" className="mt-5">
          <EmployeeHistoryTab cv={cv} employeeId={id} onOpenFeedback={setOpenFb} />
        </TabsContent>
      </Tabs>

      <FeedbackDetailSheet feedbackId={openFb} onClose={() => setOpenFb(null)} />

      <EmployeeEditDialog open={editOpen} onOpenChange={setEditOpen} cv={cv} locations={locations}
        employeeId={id} onSaved={load} />
    </div>
  );
}
